/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePreloadedQuery } from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import PortfolioPropertyMemberList from 'src/components/Portfolio/PortfolioShow/PortfolioPropertyMemberList';
import PortfolioPropertyMemberListPlaceholder from 'src/components/Portfolio/PortfolioShow/PortfolioPropertyMemberList/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import buildPortfolioPropertyMemberListRetryVariables from './helpers/buildPortfolioPropertyMemberListRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'portfolio.portfolio_show.portfolio_property_member_list.initial_load_error.message',
    defaultMessage: 'An error occurred while loading the portfolio properties. Please try again.',
  },
});

export const portfolioPropertyMemberListRouteQuery = graphql`
  query PortfolioPropertyMemberListRoute_Query(
    $uuid: ID!
    $count: Int
    $cursor: String
    $start: Timestamp!
    $finish: Timestamp!
  ) {
    portfolio(uuid: $uuid) {
      ...PortfolioPropertyMemberList_portfolio
        @arguments(count: $count, cursor: $cursor, start: $start, finish: $finish)
    }
  }
`;

const routePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({ uuid: PropTypes.string }),
  }),
});

/**
 * Renders the property member list from a manually retried query reference.
 * @param {object} props - Renderer props.
 * @param {object} props.routeProps - Found-relay route props.
 * @param {object} props.queryRef - Preloaded Relay query reference.
 * @returns {React.ReactNode} Route content.
 */
function PortfolioPropertyMemberListPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(portfolioPropertyMemberListRouteQuery, queryRef);
  return <PortfolioPropertyMemberList {...routeProps} {...queryData} />;
}

PortfolioPropertyMemberListPreloadedQueryRenderer.propTypes = {
  routeProps: routePropsShape.isRequired,
  queryRef: PropTypes.shape({ dispose: PropTypes.func }).isRequired,
};

/**
 * Renders the portfolio property member list with retry and field-error handling.
 * @param {object} props - Renderer props.
 * @param {object} props.routeProps - Found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} Route content.
 */
function PortfolioPropertyMemberListRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    portfolioPropertyMemberListRouteQuery,
    routeProps,
    {
      logLabel: 'PortfolioPropertyMemberListRoute',
      getRetryVariablesFunc: buildPortfolioPropertyMemberListRetryVariables,
    },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={PortfolioPropertyMemberListPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <PortfolioPropertyMemberListPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <PortfolioPropertyMemberList {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

PortfolioPropertyMemberListRouteRenderer.propTypes = {
  routeProps: routePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the PortfolioPropertyMemberList route state supplied by found-relay.
 * Network errors are handled before pending props so the local error boundary mounts.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} PortfolioPropertyMemberList route content or loading state.
 */
export function portfolioPropertyMemberListRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <PortfolioPropertyMemberListRouteRenderer
        key={routeProps.match.params.uuid}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <PortfolioPropertyMemberListRouteRenderer
      key={props.match.params.uuid}
      routeProps={props}
    />
  );
}

export default PortfolioPropertyMemberListRouteRenderer;
